/**
 * Global Catalog Hooks
 *
 * El catalogo global se invalida cuando cambia (afecta a todas las IPS),
 * por eso tambien invalidamos el query del aprovisionamiento por IPS y
 * el catalogo legacy de institutions.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tokenStorage } from '@/lib/api'
import * as service from './global-catalog.service'
import type {
  CreateGlobalStudyTypeInput,
  UpdateGlobalStudyTypeInput,
  CreateGlobalPriorityInput,
  UpdateGlobalPriorityInput,
} from './global-catalog.service'

const keys = {
  all: ['global-catalog'] as const,
  studyTypes: () => [...keys.all, 'study-types'] as const,
  priorities: () => [...keys.all, 'priorities'] as const,
}

const invalidateGlobalCatalog = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: keys.all })
  // El catalogo global tambien lo consume el endpoint /institutions/:id/catalog/global
  queryClient.invalidateQueries({ queryKey: ['institutions', 'catalog', 'global'] })
}

// Study Types
export function useGlobalStudyTypes() {
  const hasToken = !!tokenStorage.getAccessToken()
  return useQuery({
    queryKey: keys.studyTypes(),
    queryFn: service.listGlobalStudyTypes,
    enabled: hasToken,
    staleTime: 60 * 1000,
  })
}

export function useCreateGlobalStudyType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateGlobalStudyTypeInput) => service.createGlobalStudyType(data),
    onSuccess: () => invalidateGlobalCatalog(queryClient),
  })
}

export function useUpdateGlobalStudyType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGlobalStudyTypeInput }) =>
      service.updateGlobalStudyType(id, data),
    onSuccess: () => invalidateGlobalCatalog(queryClient),
  })
}

export function useDeleteGlobalStudyType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => service.deleteGlobalStudyType(id),
    onSuccess: () => invalidateGlobalCatalog(queryClient),
  })
}

// Priorities
export function useGlobalPriorities() {
  const hasToken = !!tokenStorage.getAccessToken()
  return useQuery({
    queryKey: keys.priorities(),
    queryFn: service.listGlobalPriorities,
    enabled: hasToken,
    staleTime: 60 * 1000,
  })
}

export function useCreateGlobalPriority() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateGlobalPriorityInput) => service.createGlobalPriority(data),
    onSuccess: () => invalidateGlobalCatalog(queryClient),
  })
}

export function useUpdateGlobalPriority() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGlobalPriorityInput }) =>
      service.updateGlobalPriority(id, data),
    onSuccess: () => invalidateGlobalCatalog(queryClient),
  })
}

export function useDeleteGlobalPriority() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => service.deleteGlobalPriority(id),
    onSuccess: () => invalidateGlobalCatalog(queryClient),
  })
}
